import React, {
  MouseEvent,
  useState,
} from 'react';
import clsx from 'clsx';
import isNil from 'lodash/isNil';
import CloseIcon from '@mui/icons-material/Close';
import {
  useNavigate,
} from 'react-router-dom';
import {
  Button,
} from '@populateui/ui';
import {
  AlertAction,
  PatientAlertDTO,
  PatientDTO,
} from 'dtos';
import {
  AlertActionExecution,
} from 'dtos/alertActionExecution';
import {
  AlertActionTypes,
} from 'dtos/alertActionTypes';
import IconButton from 'core/components/IconButton';
import {
  useNotify,
} from 'core/hooks';
import {
  useRetryMeetingCreationForAppointment,
} from 'pages/Dashboard/pages/Appointments/pages/List/hooks';
import {
  getPatientChartPath,
} from 'pages/Dashboard/pages/Charts/utils';
import {
  PatientCompact,
} from 'pages/Dashboard/pages/Encounters/types/patient';
import {
  appointmentsEventChannel,
  patientAlertsEventChannel,
  rtmEventChannel,
} from 'pages/Dashboard/services/eventChannels';
import {
  notEmptyString,
} from '@populateui/utils';
import dayjs from 'utils/dayjs';
import PATIENT_ALERT_ADDITIONAL_DATA_KEYS from 'views/EMR/PatientAlerts/constants';
import {
  useExecuteAlertAction,
} from 'views/EMR/PatientAlerts/hooks';
import {
  getDefaultAppointment,
  getDefaultPatient,
} from 'views/EMR/stores/entities';

type Props = {
  alert: PatientAlertDTO;
  patientId: number;
  patient?: PatientCompact | Partial<PatientDTO> | null;
  encounterId?: number;
  className?: string;
  size?: 'small' | 'medium';
};

type PendingKey = `${number}:${string}`;

function toPendingKey(alertId: number, actionType: AlertActionTypes): PendingKey {
  return `${alertId}:${actionType}`;
}

function isDismissAction(action: AlertAction): boolean {
  return action.type === AlertActionTypes.Dismiss;
}

const ACTION_SIZE_STYLES: Record<'small' | 'medium', string> = {
  medium: 'gap-2 pt-0.5',
  small: 'gap-1.5 pt-0',
};

export default function AlertActions({
  alert,
  patientId,
  patient,
  encounterId,
  className,
  size = 'medium',
}: Props) {
  const navigate = useNavigate();
  const {
    mutateAsync: executeAlertAction,
  } = useExecuteAlertAction();
  const [pendingKey, setPendingKey] = useState<PendingKey | null>(null);
  const notify = useNotify();

  const {
    availableActions,
    alertId,
    appointment,
  } = alert;

  const dismissAction = availableActions?.find(isDismissAction);
  const primaryActions = availableActions?.filter((action) => !isDismissAction(action));
  const telehealthEncounterId = encounterId ?? appointment?.encounterId ?? 0;
  const {
    mutateAsync: retryMeetingCreation,
    isPending: isRetryingMeeting,
  } = useRetryMeetingCreationForAppointment(telehealthEncounterId);

  function isActionDisabled(actionType?: AlertActionTypes): boolean {
    if (isNil(actionType) || isNil(alertId)) {
      return true;
    }

    return !isNil(pendingKey);
  }

  function resolvePatient(): PatientCompact {
    if (!isNil(patient)) {
      return {
        ...patient,
        patientId: patient.patientId ?? patientId,
      } as PatientCompact;
    }

    return (getDefaultPatient() ?? { patientId }) as PatientCompact;
  }

  const setupMeeting = async () => {
    if (telehealthEncounterId <= 0 || isRetryingMeeting) {
      return;
    }

    setPendingKey(toPendingKey(alertId ?? 0, AlertActionTypes.Resolve));

    try {
      await retryMeetingCreation();
      patientAlertsEventChannel.emit(
        'onPatientAlertsChanged',
        patientId,
        telehealthEncounterId,
      );
      notify({
        variant: 'success',
        message: 'Telehealth meeting created.',
      });
    } catch {
      console.error('Error creating telehealth meeting');
    } finally {
      setPendingKey(null);
    }
  };

  const runAction = async (action: AlertAction) => {
    if (!isNil(action.type) && !isNil(alertId)) {
      const key = toPendingKey(alertId, action.type);
      setPendingKey(key);

      try {
        if (action.type === AlertActionTypes.GoToInsurance) {
          if (patientId > 0) {
            navigate(getPatientChartPath(patientId, 'insurance')!, { replace: true });
          }
        } else if (action.type === AlertActionTypes.SetUpTelehealthMeeting) {
          await setupMeeting();
        } else if (action.type === AlertActionTypes.ShowPatientRTMTrends) {
          if (patientId > 0) {
            rtmEventChannel.emit('onShowRTMPatientTrends', {
              patient: resolvePatient(),
            });
          }
        } else if (action.type === AlertActionTypes.ScheduleFollowUpAndResolve) {
          const rawDate = alert.additionalData
            ?.[PATIENT_ALERT_ADDITIONAL_DATA_KEYS.suggestedFollowUpDate];
          const suggestedDate = typeof rawDate === 'string' ? rawDate : undefined;

          let defaultStartDateTime;
          if (suggestedDate && dayjs(suggestedDate).isValid()) {
            const now = dayjs.tz();
            defaultStartDateTime = dayjs.tz(suggestedDate)
              .hour(now.hour())
              .minute(now.minute())
              .second(now.second());
          }

          const resolvedPatient = resolvePatient();
          const defaultAppointment = getDefaultAppointment();
          const followUpReason = notEmptyString(defaultAppointment?.reason)
            ? `${defaultAppointment?.reason ?? ''} - Follow-up`
            : undefined;

          appointmentsEventChannel.emit('onEventCreate', {
            ...(defaultStartDateTime ? { defaultStartDateTime } : {}),
            defaultPatient: resolvedPatient as Partial<PatientDTO>,
            ...(defaultAppointment?.doctor ? { defaultDoctor: defaultAppointment.doctor } : {}),
            ...(defaultAppointment?.serviceFacility
              ? { defaultServiceFacility: defaultAppointment.serviceFacility }
              : {}),
            ...(followUpReason ? { defaultReason: followUpReason } : {}),
            onAppointmentCreated: async () => {
              await executeAlertAction({
                alertId,
                actionType: action.type,
              });
              patientAlertsEventChannel.emit(
                'onPatientAlertsChanged',
                patientId,
                encounterId,
              );
            },
          });
        } else if (action.execution === AlertActionExecution.Backend) {
          const result = await executeAlertAction({
            alertId,
            actionType: action.type,
          });
          patientAlertsEventChannel.emit(
            'onPatientAlertsChanged',
            patientId,
            encounterId,
          );
          notify({
            variant: 'success',
            message: result?.message ?? 'Alert resolved successfully.',
          });
        }
      } catch {
        // React Query/global handlers surface API action failures.
      } finally {
        setPendingKey(null);
      }
    }
  };

  function handleActionClick(
    event: MouseEvent<HTMLElement>,
    action: AlertAction,
  ) {
    event.stopPropagation();
    runAction(action);
  }

  return (
    <div
      className={clsx('flex items-center shrink-0', ACTION_SIZE_STYLES[size], className)}
      onClick={(event) => { event.stopPropagation(); }}
      onKeyDown={(event) => { event.stopPropagation(); }}
      role="presentation"
    >
      {primaryActions?.map((action) => (
        <Button
          key={action.type}
          variant="contained"
          size="small"
          disabled={isActionDisabled(action.type)}
          onClick={(event) => { handleActionClick(event, action); }}
        >
          {action.label}
        </Button>
      ))}
      {!isNil(dismissAction) && (
        <IconButton
          tooltip="Dismiss"
          size="small"
          className="!mt-0.5 !text-gray-600 hover:!text-gray-600"
          disabled={isActionDisabled(dismissAction.type)}
          onClick={(event) => { handleActionClick(event, dismissAction); }}
        >
          <CloseIcon className="!w-4 !h-4" />
        </IconButton>
      )}
    </div>
  );
}
